import React from 'react'
import { Tabs } from '@surecompanies/core_components'
import type { TabsProps } from '@surecompanies/core_components'
import { useRouterContext } from 'components/RouterContext'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import {
  BILLING_PROFILE_TAB_KEYS,
  billingProfileTabPath,
  type BillingProfileTabKey,
} from '../constants'

interface BillingProfileTabsHeaderProps {
  activeTab: BillingProfileTabKey
}

/**
 * Tab header shared by the Company and User pages. Each page owns its own
 * `Page` and breadcrumb and passes its own key, matching
 * `PlanOverridesTabsHeader`.
 */
const BillingProfileTabsHeader = ({
  activeTab,
}: BillingProfileTabsHeaderProps) => {
  const { router } = useRouterContext()
  const { flags } = useFeatureFlags()
  const { allowed: canManageOnboardingUser } = usePermission(
    RbacActions.Manage,
    RbacSubjects.OnboardingUser
  )

  // Same gate the Invoice access button used before this became a tab.
  const showFundingManagers =
    Boolean(flags['admin.finance.user-invitations-enabled']) &&
    canManageOnboardingUser

  const items: TabsProps['items'] = [
    { key: BILLING_PROFILE_TAB_KEYS.COMPANIES, label: 'Company' },
    ...(showFundingManagers
      ? [
          {
            key: BILLING_PROFILE_TAB_KEYS.FUNDING_MANAGERS,
            label: 'Finance managers',
          },
        ]
      : []),
  ]

  const handleChange = (key: string) => {
    if (key === activeTab) return
    router.push(billingProfileTabPath(key as BillingProfileTabKey))
  }

  return (
    <Tabs
      activeKey={activeTab}
      items={items}
      onChange={handleChange}
      type="line"
    />
  )
}

export { BillingProfileTabsHeader }
